/**
 * AgentsSettingsView — ניהול סוכנים
 *
 * Phase 10: Full CRUD interface for the  inventory_agents  table.
 *
 * Columns managed:
 *   name                     — text, required
 *   phone                    — text, optional
 *   email                    — text, optional
 *   sales_commission_rate    — numeric (%), optional  (אחוז עמלה ממכירות)
 *   collection_commission_rate — numeric (%), optional (אחוז עמלה מגביה)
 *   notes                    — textarea, optional
 */

import { useEffect, useState, useCallback } from 'react';
import { Briefcase, Plus, Pencil, Trash2, X, Loader as Loader2, CircleAlert as AlertCircle, Phone, Mail, FileText, Users, ShoppingCart, Banknote, Search, Crown, Warehouse } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InventoryAgent {
  id:                         string;
  company_id:                 string;
  name:                       string;
  phone:                      string | null;
  email:                      string | null;
  sales_commission_rate:      number | null;
  collection_commission_rate: number | null;
  is_master_agent:            boolean;
  default_warehouse_id:       string | null;
  default_warehouse_name?:    string | null;
  notes:                      string | null;
  created_at:                 string;
  updated_at:                 string;
}

interface AgentForm {
  name:                       string;
  phone:                      string;
  email:                      string;
  sales_commission_rate:      number | null;
  collection_commission_rate: number | null;
  is_master_agent:            boolean;
  default_warehouse_id:       string | null;
  notes:                      string;
}

interface WarehouseOption {
  id: string;
  name: string;
}

const EMPTY_FORM: AgentForm = {
  name:                       '',
  phone:                      '',
  email:                      '',
  sales_commission_rate:      null,
  collection_commission_rate: null,
  is_master_agent:            false,
  default_warehouse_id:       null,
  notes:                      '',
};

interface AgentsSettingsViewProps {
  companyId: string | null;
  showToast: (msg: string, ok?: boolean) => void;
}

// ─── Small helper: percentage badge ──────────────────────────────────────────

function CommBadge({
  value,
  icon,
  color,
  label,
}: {
  value: number | null;
  icon:  React.ReactNode;
  color: string;   // tailwind colour token e.g. "teal" | "indigo"
  label: string;
}) {
  if (value === null) return <span className="text-gray-300 text-xs">—</span>;
  return (
    <span
      title={label}
      className={`inline-flex items-center gap-1 px-2 py-0.5
                  bg-${color}-50 text-${color}-700 text-xs font-semibold rounded-full`}
    >
      {icon}
      {value}%
    </span>
  );
}

// ─── Number field helper ──────────────────────────────────────────────────────

function PercentInput({
  label,
  hint,
  icon,
  value,
  onChange,
}: {
  label:    string;
  hint:     string;
  icon:     React.ReactNode;
  value:    number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
        {icon}
        {label}
        <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
      </label>
      <p className="text-xs text-gray-400 -mt-0.5">{hint}</p>
      <div className="relative">
        <input
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={value ?? ''}
          onChange={e =>
            onChange(e.target.value === '' ? null : parseFloat(e.target.value) || 0)
          }
          placeholder="0.0"
          dir="ltr"
          className="w-full pl-8 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-teal-400
                     focus:border-transparent"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2
                         text-gray-400 text-sm pointer-events-none">%</span>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AgentsSettingsView({ companyId, showToast }: AgentsSettingsViewProps) {

  // ── State ──────────────────────────────────────────────────────────────────
  const [agents,    setAgents]    = useState<InventoryAgent[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing,   setEditing]   = useState<InventoryAgent | null>(null);
  const [form,      setForm]      = useState<AgentForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);

  const [delTarget, setDelTarget] = useState<InventoryAgent | null>(null);
  const [deleting,  setDeleting]  = useState(false);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchAgents = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    const [agentsRes, whRes] = await Promise.all([
      supabase
        .from('inventory_agents')
        .select(`
          id, company_id, name, phone, email,
          sales_commission_rate, collection_commission_rate,
          is_master_agent, default_warehouse_id, notes, created_at, updated_at,
          inventory_warehouses:default_warehouse_id ( name )
        `)
        .eq('company_id', companyId)
        .order('name'),
      supabase
        .from('inventory_warehouses')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name'),
    ]);
    if (agentsRes.error) { setError(agentsRes.error.message); }
    else {
      const mapped = (agentsRes.data ?? []).map((a: any) => ({
        ...a,
        default_warehouse_name: a.inventory_warehouses?.name ?? null,
        inventory_warehouses: undefined,
      }));
      setAgents(mapped as InventoryAgent[]);
    }
    if (whRes.data) setWarehouses(whRes.data as WarehouseOption[]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (a: InventoryAgent) => {
    setEditing(a);
    setForm({
      name:                       a.name,
      phone:                      a.phone ?? '',
      email:                      a.email ?? '',
      sales_commission_rate:      a.sales_commission_rate,
      collection_commission_rate: a.collection_commission_rate,
      is_master_agent:            a.is_master_agent ?? false,
      default_warehouse_id:       a.default_warehouse_id,
      notes:                      a.notes ?? '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const setField = <K extends keyof AgentForm>(k: K, v: AgentForm[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (!form.name.trim()) return 'שם הסוכן הוא שדה חובה';
    const em = form.email.trim();
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return 'כתובת אימייל אינה תקינה';
    if (
      form.sales_commission_rate !== null &&
      (form.sales_commission_rate < 0 || form.sales_commission_rate > 100)
    ) return 'אחוז עמלה ממכירות חייב להיות בין 0 ל-100';
    if (
      form.collection_commission_rate !== null &&
      (form.collection_commission_rate < 0 || form.collection_commission_rate > 100)
    ) return 'אחוז עמלה מגביה חייב להיות בין 0 ל-100';
    return null;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true); setFormError(null);

    const payload = {
      name:                       form.name.trim(),
      phone:                      form.phone.trim()  || null,
      email:                      form.email.trim()  || null,
      sales_commission_rate:      form.sales_commission_rate,
      collection_commission_rate: form.collection_commission_rate,
      is_master_agent:            form.is_master_agent,
      default_warehouse_id:       form.default_warehouse_id || null,
      notes:                      form.notes.trim()  || null,
    };

    try {
      if (editing) {
        const { error: e } = await supabase
          .from('inventory_agents')
          .update(payload)
          .eq('id', editing.id);
        if (e) throw e;
        showToast('סוכן עודכן בהצלחה');
      } else {
        const { error: e } = await supabase
          .from('inventory_agents')
          .insert({ company_id: companyId, ...payload });
        if (e) throw e;
        showToast('סוכן נוסף בהצלחה');
      }
      closeModal();
      fetchAgents();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    const { error: e } = await supabase
      .from('inventory_agents')
      .delete()
      .eq('id', delTarget.id);
    if (e) { showToast(e.message, false); }
    else   {
      showToast('סוכן נמחק — לקוחות משויכים שוחררו אוטומטית');
      setDelTarget(null);
      fetchAgents();
    }
    setDeleting(false);
  };

  const filteredAgents = agents.filter(a => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.trim().toLowerCase();
    return (a.name?.toLowerCase().includes(q)) ||
           (a.phone?.includes(q)) ||
           (a.email?.toLowerCase().includes(q));
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Briefcase className="w-5 h-5 text-teal-600" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">ניהול סוכנים</h2>
              <p className="text-xs text-gray-500">סוכני מכירות — ניתן לשייכם ללקוחות</p>
            </div>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            הוסף סוכן
          </button>
        </div>

        {!loading && agents.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="חיפוש לפי שם, טלפון או אימייל..."
                className="w-full pr-10 pl-4 py-2 text-sm border border-gray-200 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400
                           placeholder:text-gray-400 transition-colors"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-3 mx-6 my-4 px-4 py-3 bg-red-50
                          border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />{error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-14 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">טוען סוכנים…</span>
          </div>
        ) : agents.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <Users className="w-10 h-10 text-gray-200" />
            <p className="text-sm font-medium">אין סוכנים עדיין</p>
            <p className="text-xs text-gray-400">לחץ על "הוסף סוכן" כדי להוסיף את הסוכן הראשון</p>
          </div>
        ) : (
          /* Table */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold
                               text-gray-500 uppercase tracking-wide">
                  <th className="px-6 py-3 text-right">שם</th>
                  <th className="px-6 py-3 text-right">טלפון</th>
                  <th className="px-6 py-3 text-right">אימייל</th>
                  <th className="px-6 py-3 text-right">
                    <span className="flex items-center gap-1">
                      <ShoppingCart className="w-3 h-3" />
                      עמלת מכירות
                    </span>
                  </th>
                  <th className="px-6 py-3 text-right">
                    <span className="flex items-center gap-1">
                      <Banknote className="w-3 h-3" />
                      עמלת גביה
                    </span>
                  </th>
                  <th className="px-6 py-3 text-right">הערות</th>
                  <th className="px-6 py-3 text-right w-28">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredAgents.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50/60 transition-colors">

                    {/* Name + avatar */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center
                                        justify-center text-teal-700 text-xs font-bold shrink-0">
                          {a.name[0]?.toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-900">{a.name}</span>
                        {a.is_master_agent && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50
                                           text-amber-700 text-[10px] font-bold rounded-full mr-1.5">
                            <Crown className="w-3 h-3" />
                            סוכן ראשי
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Phone */}
                    <td className="px-6 py-4 text-gray-500 text-xs" dir="ltr">
                      {a.phone ? (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400" />{a.phone}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Email */}
                    <td className="px-6 py-4 text-gray-500 text-xs" dir="ltr">
                      {a.email ? (
                        <a href={`mailto:${a.email}`}
                           className="flex items-center gap-1 hover:text-blue-600 transition-colors">
                          <Mail className="w-3 h-3 text-gray-400" />{a.email}
                        </a>
                      ) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Sales commission */}
                    <td className="px-6 py-4">
                      <CommBadge
                        value={a.sales_commission_rate}
                        icon={<ShoppingCart className="w-2.5 h-2.5" />}
                        color="teal"
                        label="אחוז עמלה ממכירות"
                      />
                    </td>

                    {/* Collection commission */}
                    <td className="px-6 py-4">
                      <CommBadge
                        value={a.collection_commission_rate}
                        icon={<Banknote className="w-2.5 h-2.5" />}
                        color="indigo"
                        label="אחוז עמלה מגביה"
                      />
                    </td>

                    {/* Notes */}
                    <td className="px-6 py-4 text-gray-400 text-xs max-w-[160px] truncate">
                      {a.notes || <span className="text-gray-200">—</span>}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(a)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                     rounded-lg transition-colors"
                          title="ערוך"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDelTarget(a)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50
                                     rounded-lg transition-colors"
                          title="מחק"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {agents.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400">
            {agents.length} סוכנים · שייך סוכן ללקוח בלשונית "ניהול לקוחות"
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          Add / Edit Modal
          ══════════════════════════════════════════════════════════════════════ */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6
                          space-y-5 max-h-[90vh] overflow-y-auto">

            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {editing ? 'עריכת סוכן' : 'הוספת סוכן חדש'}
              </h3>
              <button
                onClick={closeModal}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {formError && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50
                              border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />{formError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">

              {/* Name — full width */}
              <div className="col-span-2 space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  שם הסוכן <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  placeholder="לדוגמה: יוסי כהן"
                  autoFocus
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-teal-400
                             focus:border-transparent"
                />
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-gray-400" />
                  טלפון
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setField('phone', e.target.value)}
                  placeholder="050-0000000"
                  dir="ltr"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-teal-400
                             focus:border-transparent"
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-gray-400" />
                  אימייל
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setField('email', e.target.value)}
                  placeholder="agent@example.com"
                  dir="ltr"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-teal-400
                             focus:border-transparent"
                />
              </div>

              {/* Sales Commission % */}
              <PercentInput
                label="אחוז עמלה ממכירות"
                hint="עמלה על ערך ההזמנה שהסוכן הביא"
                icon={<ShoppingCart className="w-3.5 h-3.5 text-teal-500" />}
                value={form.sales_commission_rate}
                onChange={v => setField('sales_commission_rate', v)}
              />

              {/* Collection Commission % */}
              <PercentInput
                label="אחוז עמלה מגביה"
                hint="עמלה על גביית חוב / אשראי"
                icon={<Banknote className="w-3.5 h-3.5 text-indigo-500" />}
                value={form.collection_commission_rate}
                onChange={v => setField('collection_commission_rate', v)}
              />

              {/* Default Warehouse — full width */}
              <div className="col-span-2 space-y-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Warehouse className="w-3.5 h-3.5 text-gray-400" />
                  מחסן ברירת מחדל
                  <span className="text-xs font-normal text-gray-400">(למכירה מהרכב)</span>
                </label>
                <select
                  value={form.default_warehouse_id ?? ''}
                  onChange={e => setField('default_warehouse_id', e.target.value || null)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-teal-400
                             focus:border-transparent"
                >
                  <option value="">ללא — לא משויך למחסן</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>

              {/* Master Agent toggle — full width */}
              <div className="col-span-2">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={form.is_master_agent}
                      onChange={e => setField('is_master_agent', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-amber-500
                                    transition-colors" />
                    <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow
                                    transition-transform peer-checked:translate-x-5" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <Crown className="w-3.5 h-3.5 text-amber-500" />
                      סוכן ראשי (מנהל מכירות)
                    </span>
                    <p className="text-xs text-gray-400 mt-0.5">
                      סוכן ראשי רואה את כל הלקוחות הפתוחים ל-B2B, ללא צורך בשיוך ידני
                    </p>
                  </div>
                </label>
              </div>

              {/* Notes — full width */}
              <div className="col-span-2 space-y-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-gray-400" />
                  הערות
                  <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                </label>
                <textarea
                  value={form.notes}
                  onChange={e => setField('notes', e.target.value)}
                  placeholder="אזור פעילות, מידע נוסף על הסוכן…"
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             resize-none focus:outline-none focus:ring-2 focus:ring-teal-400
                             focus:border-transparent"
                />
              </div>

            </div>{/* end grid */}

            <div className="flex items-center gap-3 justify-end pt-1">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'שמור שינויים' : 'הוסף סוכן'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Delete Confirm Modal
          ══════════════════════════════════════════════════════════════════════ */}
      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת סוכן</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את{' '}
                  <span className="font-semibold text-gray-800">{delTarget.name}</span>?
                  {' '}לקוחות המשויכים לסוכן זה ישוחררו אוטומטית (agent_id יאופס).
                  פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setDelTarget(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                           disabled:opacity-50 text-white text-sm font-medium
                           rounded-lg transition-colors"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
